import { useState, useCallback, useRef } from 'react';

interface UseInlineEditOptions {
  /** Current value of the field */
  currentValue: string;
  /** Called with new value on save. Should throw on error. */
  onSave: (value: string) => Promise<void>;
  /** Whether field is multiline (Enter inserts newline instead of saving) */
  isMultiline?: boolean;
  /** Called when editing starts */
  onEditStart?: () => void;
  /** Called when editing ends (save or cancel) */
  onEditEnd?: () => void;
}

interface UseInlineEditReturn {
  isEditing: boolean;
  isSaving: boolean;
  editValue: string;
  setEditValue: (value: string) => void;
  startEditing: () => void;
  cancelEditing: () => void;
  handleSave: () => Promise<void>;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handleBlur: () => void;
}

export function useInlineEdit({
  currentValue,
  onSave,
  isMultiline = false,
  onEditStart,
  onEditEnd,
}: UseInlineEditOptions): UseInlineEditReturn {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editValue, setEditValue] = useState(currentValue);
  const cancelledRef = useRef(false);

  const startEditing = useCallback(() => {
    setEditValue(currentValue);
    setIsEditing(true);
    cancelledRef.current = false;
    onEditStart?.();
  }, [currentValue, onEditStart]);

  const cancelEditing = useCallback(() => {
    cancelledRef.current = true;
    setEditValue(currentValue);
    setIsEditing(false);
    onEditEnd?.();
  }, [currentValue, onEditEnd]);

  const handleSave = useCallback(async () => {
    if (cancelledRef.current) return;

    const trimmed = editValue.trim();
    // Skip API call if value unchanged
    if (trimmed === (currentValue || '').trim()) {
      setIsEditing(false);
      onEditEnd?.();
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
      onEditEnd?.();
    } catch {
      // Stay in edit mode on error — toast handled by caller
    } finally {
      setIsSaving(false);
    }
  }, [editValue, currentValue, onSave, onEditEnd]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    } else if (e.key === 'Enter' && !isMultiline) {
      e.preventDefault();
      handleSave();
    }
  }, [cancelEditing, handleSave, isMultiline]);

  const handleBlur = useCallback(() => {
    // Small delay to let onMouseDown cancel fire first
    setTimeout(() => {
      if (!cancelledRef.current && isEditing) {
        handleSave();
      }
    }, 0);
  }, [handleSave, isEditing]);

  return {
    isEditing,
    isSaving,
    editValue,
    setEditValue,
    startEditing,
    cancelEditing,
    handleSave,
    handleKeyDown,
    handleBlur,
  };
}
